import {
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import DOMPurify from "dompurify";

// --- HTML Sanitization Helpers ---

const ALLOWED_HTML_TAGS = ["p", "br", "strong", "em", "ul", "ol", "li"];

export const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: ALLOWED_HTML_TAGS,
    ALLOWED_ATTR: [],
  });

/**
 * Convert markdown to basic HTML if the text contains markdown but no HTML tags.
 * This handles cases where AI models return markdown despite being asked for HTML.
 */
const markdownToHtml = (text) => {
  if (!text) return text;
  // If it already has HTML tags, return as-is
  if (/<(p|ul|ol|li|strong|em|br)\b/i.test(text)) return text;
  // If it doesn't look like markdown, wrap in <p> and return
  if (!/[*\-#\n]/.test(text)) return `<p>${text}</p>`;

  let html = "";
  let inList = false;
  const lines = text.split("\n");

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      continue;
    }
    // Bullet items
    if (/^[-*•]\s+/.test(trimmed)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const content = trimmed.replace(/^[-*•]\s+/, "");
      html += `<li>${content}</li>`;
    } else if (/^\d+\.\s+/.test(trimmed)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const content = trimmed.replace(/^\d+\.\s+/, "");
      html += `<li>${content}</li>`;
    } else {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      html += `<p>${trimmed}</p>`;
    }
  }
  if (inList) html += "</ul>";

  // Convert inline markdown: **bold** → <strong>, *italic* → <em>
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");

  return html;
};

export const decodeAndSanitizeHtml = (html) => {
  const txt = document.createElement("textarea");
  txt.innerHTML = html || "";
  const decoded = txt.value;
  // Convert markdown to HTML if needed before sanitizing
  const asHtml = markdownToHtml(decoded);
  return sanitizeHtml(asHtml);
};

// --- Risk Level Helpers ---

export const isHighRisk = (riskLevel) => {
  const level = riskLevel?.toLowerCase();
  return level === "high" || level === "danger";
};

export const isMediumRisk = (riskLevel) => {
  const level = riskLevel?.toLowerCase();
  return level === "medium" || level === "warning";
};

export const getRiskLevelIcon = (riskLevel) => {
  if (isHighRisk(riskLevel)) return faTimesCircle;
  if (isMediumRisk(riskLevel)) return faExclamationTriangle;
  const level = riskLevel?.toLowerCase();
  if (level === "low" || level === "safe") return faCheckCircle;
  return faQuestionCircle;
};

export const getRiskLevelClass = (riskLevel) => {
  if (isHighRisk(riskLevel)) return "risk-high";
  if (isMediumRisk(riskLevel)) return "risk-medium";
  const level = riskLevel?.toLowerCase();
  if (level === "low" || level === "safe") return "risk-low";
  return "risk-unknown";
};

// Short risk class without prefix (e.g., "high", "medium", "low", "unknown")
export const getRiskShortClass = (riskLevel) => {
  if (isHighRisk(riskLevel)) return "high";
  if (isMediumRisk(riskLevel)) return "medium";
  const level = riskLevel?.toLowerCase();
  if (level === "low" || level === "safe") return "low";
  return "unknown";
};

// --- Threat Banner Helpers ---

export const getThreatBannerClass = (riskLevel) => {
  if (isHighRisk(riskLevel) || isMediumRisk(riskLevel))
    return "threat-detected";
  return "safe";
};

export const getThreatTitle = (riskLevel) => {
  if (isHighRisk(riskLevel)) return "SCAM DETECTED";
  if (isMediumRisk(riskLevel)) return "POTENTIAL THREAT DETECTED";
  return "NO THREATS DETECTED";
};

export const getThreatSubtitle = (riskLevel, riskType) => {
  if (riskType) return `Type: ${riskType}`;
  if (isHighRisk(riskLevel))
    return "This message contains suspicious or fraudulent content";
  if (isMediumRisk(riskLevel))
    return "This message shows some indicators of suspicious activity";
  return "This message appears to be safe";
};

// --- Scam Type Formatting ---

export const formatScamType = (scamType) => {
  return scamType
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// --- Overall Analysis Aggregation ---

export const getOverallRiskLevel = (analysisResults) => {
  if (!analysisResults) return null;

  const riskPriority = {
    high: 4,
    danger: 4,
    medium: 3,
    warning: 3,
    low: 2,
    safe: 2,
    unknown: 1,
  };

  // Backend computes overallRiskLevel including ORE safety-net escalation.
  // Use it as a floor — never return a lower risk than the backend determined.
  const backendRisk = analysisResults.overallRiskLevel?.toLowerCase();
  const backendLevel = riskPriority[backendRisk] || 0;

  const phoneRisk = analysisResults.phoneAnalysis?.riskLevel?.toLowerCase();
  const textRisk = analysisResults.textAnalysis?.riskLevel?.toLowerCase();
  const phoneLevel = riskPriority[phoneRisk] || 0;
  const textLevel = riskPriority[textRisk] || 0;

  // Pick the highest risk among backend, phone, and text
  const maxClientLevel = Math.max(phoneLevel, textLevel);

  if (backendLevel >= maxClientLevel && backendRisk) {
    return analysisResults.overallRiskLevel;
  } else if (phoneLevel >= textLevel && phoneRisk) {
    return analysisResults.phoneAnalysis.riskLevel;
  } else if (textRisk) {
    return analysisResults.textAnalysis.riskLevel;
  }

  return backendRisk ? analysisResults.overallRiskLevel : "Unknown";
};

export const getOverallRiskType = (analysisResults) => {
  if (!analysisResults) return null;

  if (analysisResults.textAnalysis?.category) {
    return analysisResults.textAnalysis.category;
  }
  if (analysisResults.phoneAnalysis?.category) {
    return analysisResults.phoneAnalysis.category;
  }
  if (analysisResults.textAnalysis?.riskFactors?.length > 0) {
    return analysisResults.textAnalysis.riskFactors[0];
  }
  return null;
};

export const getOverallRecommendation = (analysisResults) => {
  if (!analysisResults) return null;

  if (analysisResults.textAnalysis?.suggestions) {
    return analysisResults.textAnalysis.suggestions;
  }
  if (analysisResults.phoneAnalysis?.recommendation) {
    return analysisResults.phoneAnalysis.recommendation;
  }

  const riskLevel = getOverallRiskLevel(analysisResults)?.toLowerCase();
  switch (riskLevel) {
    case "high":
    case "danger":
      return "Exercise extreme caution and avoid responding to this message.";
    case "medium":
    case "warning":
      return "Proceed with caution and verify the sender before taking any action.";
    case "low":
    case "safe":
      return "This appears to be safe, but always stay vigilant.";
    default:
      return "Unable to determine safety due to insufficient information. Use your best judgment.";
  }
};
